import { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Sparkles, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "agastya_session_id";

const genSessionId = () =>
  `is-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = genSessionId();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const INTRO_MESSAGE = {
  role: "assistant",
  content:
    "Namaste. I'm Agastya — the AI concierge for IngredientSignal. I can explain how our sourcing intelligence works, help you decide whether a Buyer or Supplier application is the right fit, and help you shape your request before you submit it. What are you working on?",
  intro: true,
};

const SUGGESTIONS = [
  "How does IngredientSignal work?",
  "I'm sourcing a botanical extract — where do I start?",
  "I'm a supplier — what does representation look like?",
];

const MessageBubble = ({ role, content, testId }) => {
  const isUser = role === "user";
  return (
    <div
      data-testid={testId}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-brand-olive text-brand-cream rounded-2xl rounded-br-sm"
            : "bg-brand-linen text-brand-warm rounded-2xl rounded-bl-sm"
        }`}
      >
        {content || (
          <span className="inline-flex items-center gap-1 text-brand-muted">
            <span className="w-1.5 h-1.5 bg-brand-olive rounded-full animate-pulse" />
            <span className="w-1.5 h-1.5 bg-brand-olive rounded-full animate-pulse [animation-delay:0.15s]" />
            <span className="w-1.5 h-1.5 bg-brand-olive rounded-full animate-pulse [animation-delay:0.3s]" />
          </span>
        )}
      </div>
    </div>
  );
};

export default function Agastya() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  // Init session + load history once
  useEffect(() => {
    sessionIdRef.current = getSessionId();
    (async () => {
      try {
        const res = await fetch(
          `${API}/agastya/history/${sessionIdRef.current}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(
            data.messages.map((m) => ({ role: m.role, content: m.content }))
          );
        }
      } catch (e) {
        // silent — intro message stays
      }
    })();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Auto-scroll on new message
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;

      setInput("");
      setMessages((prev) => [
        ...prev,
        { role: "user", content: trimmed },
        { role: "assistant", content: "" },
      ]);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch(`${API}/agastya/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            message: trimmed,
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          throw new Error(`HTTP ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by "\n\n"
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const frame of parts) {
            const line = frame.trim();
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            try {
              const evt = JSON.parse(payload);
              if (evt.delta) {
                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last && last.role === "assistant") {
                    next[next.length - 1] = {
                      ...last,
                      content: (last.content || "") + evt.delta,
                    };
                  }
                  return next;
                });
              } else if (evt.error) {
                setMessages((prev) => {
                  const next = [...prev];
                  next[next.length - 1] = {
                    role: "assistant",
                    content: evt.error,
                  };
                  return next;
                });
              }
            } catch {
              // ignore malformed frame
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Agastya chat failed", err);
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === "assistant" && !last.content) {
              next[next.length - 1] = {
                role: "assistant",
                content:
                  "Agastya is momentarily unavailable. Please try again in a moment.",
              };
            }
            return next;
          });
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [streaming]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          type="button"
          data-testid="agastya-open-button"
          onClick={() => setOpen(true)}
          aria-label="Open Agastya chat"
          className="group fixed bottom-6 right-6 z-40 inline-flex items-center gap-3 pl-4 pr-5 py-3.5 rounded-full bg-brand-olive hover:bg-brand-hover text-brand-cream shadow-[0_18px_40px_-12px_rgba(47,61,30,0.45)] transition-all duration-300 hover:translate-y-[-2px]"
        >
          <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-brand-cream text-brand-olive">
            <Sparkles className="w-4 h-4" strokeWidth={1.8} />
            <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brand-accent opacity-70 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-accent" />
            </span>
          </span>
          <span className="text-sm font-medium tracking-wide">
            Ask Agastya
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="agastya-panel"
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-[420px] h-[min(640px,80vh)] bg-brand-cream border border-brand-border shadow-[0_30px_80px_-20px_rgba(47,61,30,0.35)] rounded-2xl flex flex-col overflow-hidden"
          role="dialog"
          aria-label="Agastya — IngredientSignal AI concierge"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border bg-brand-ivory">
            <div className="flex items-center gap-3">
              <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-brand-olive text-brand-cream">
                <Sparkles className="w-4 h-4" strokeWidth={1.8} />
              </span>
              <div>
                <div className="text-sm font-medium text-brand-deep leading-tight">
                  Agastya
                </div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-brand-muted">
                  IngredientSignal Concierge
                </div>
              </div>
            </div>
            <button
              type="button"
              data-testid="agastya-close-button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-muted hover:text-brand-deep hover:bg-brand-linen transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            data-testid="agastya-message-list"
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
          >
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                role={m.role}
                content={m.content}
                testId={`agastya-msg-${i}`}
              />
            ))}
            {messages.length === 1 && messages[0].intro && (
              <div className="pt-2 space-y-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-brand-quiet">
                  Suggestions
                </p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`agastya-suggestion-${i}`}
                    onClick={() => sendMessage(s)}
                    className="block w-full text-left text-sm text-brand-warm hover:text-brand-deep px-3 py-2 rounded-lg border border-brand-border hover:border-brand-olive/40 hover:bg-brand-ivory transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            data-testid="agastya-input-form"
            className="border-t border-brand-border bg-brand-ivory p-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                rows={1}
                placeholder="Ask about sourcing, applications, or the process…"
                disabled={streaming}
                data-testid="agastya-input"
                className="flex-1 resize-none bg-brand-cream border border-brand-border rounded-xl px-3 py-2.5 text-sm text-brand-deep placeholder:text-brand-quiet/70 focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors max-h-32"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="agastya-send-button"
                aria-label="Send"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-olive hover:bg-brand-hover text-brand-cream disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-brand-quiet leading-relaxed">
              Conversations may be reviewed by FFIB to improve the service. For
              formal requests, use{" "}
              <a
                href="/get-started"
                className="text-brand-olive hover:text-brand-hover underline underline-offset-2"
              >
                Get Started
              </a>
              .
            </p>
          </form>
        </div>
      )}
    </>
  );
}
